import { useSyncExternalStore } from "react";
import { ConfigProvider } from "antd";
import { antdThemeFor, isAdeed } from "../../constants/brand";
import { ThemeMode } from "../../constants/commonConstants";
import { LocalStorageKeys } from "../../constants/localStorageKeys";
import * as localStorageService from "../../service/localStorageService";

// Light or dark (Settings > Appearance), as the ADEED portal does it (procureco src/lib/theme.js).
// The choice is light, dark or system - the default, which follows the device and keeps following it
// while the page is open. It is remembered in this browser only, and applied as <html data-mode="light|dark">:
// src/index.css holds the dark palette, brand.js antdThemeFor() the Ant Design one. Only ADEED Freight
// offers dark mode; TruKKer is always light.

const Modes = Object.values(ThemeMode);
const darkQuery =
  typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;

const readMode = () => {
  if (!isAdeed) return ThemeMode.LIGHT;
  try {
    const value = localStorageService.getValue(LocalStorageKeys.ThemeMode);
    return Modes.includes(value) ? value : ThemeMode.SYSTEM;
  } catch {
    return ThemeMode.SYSTEM; // storage blocked or unreadable: follow the device
  }
};

let mode = readMode();
const listeners = new Set();

/** What is showing: the chosen mode with "system" resolved to light or dark. */
export const resolveThemeMode = () =>
  mode === ThemeMode.SYSTEM ? (darkQuery?.matches ? ThemeMode.DARK : ThemeMode.LIGHT) : mode;

/** Puts the mode on <html> and themes antd's static toasts and confirm dialogs (they don't read ConfigProvider). */
export const applyThemeMode = () => {
  const resolved = resolveThemeMode();
  document.documentElement.dataset.mode = resolved;
  ConfigProvider.config({ theme: antdThemeFor(resolved) });
};

const changed = () => {
  applyThemeMode();
  listeners.forEach((listener) => listener());
};

/** The chosen mode: light, dark or system. */
export const getThemeMode = () => mode;

export const setThemeMode = (next) => {
  if (!isAdeed || !Modes.includes(next) || next === mode) return;
  mode = next;
  try {
    localStorageService.setKey(LocalStorageKeys.ThemeMode, next);
  } catch {
    // storage blocked: the choice holds for this visit only
  }
  changed();
};

if (isAdeed) {
  // The device switched between light and dark.
  const onSystemChange = () => mode === ThemeMode.SYSTEM && changed();
  if (darkQuery?.addEventListener) darkQuery.addEventListener("change", onSystemChange);
  else darkQuery?.addListener?.(onSystemChange);
  // Another tab changed the choice.
  window.addEventListener("storage", (e) => {
    if (e.key === LocalStorageKeys.ThemeMode) {
      mode = readMode();
      changed();
    }
  });
}

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useThemeMode = () => useSyncExternalStore(subscribe, getThemeMode);
export const useResolvedThemeMode = () => useSyncExternalStore(subscribe, resolveThemeMode);
export const useIsDarkMode = () => useResolvedThemeMode() === ThemeMode.DARK;
